/**
 * Markup sanitisation policy for HTML and SVG (§22, §30).
 *
 * **What this module is not**: a sanitiser. Stripping dangerous constructs from
 * untrusted markup safely requires a real HTML parser that mirrors the browser's
 * own quirks, and doing it with string surgery is a well-documented way to ship
 * a bypass. Core has no DOM, so the actual removal happens at the UI layer with
 * DOMPurify, configured from {@link MARKUP_POLICY} below.
 *
 * **What this module is**: the single definition of what is allowed, plus a
 * detector that reports what a file contains. The report drives the §31 file
 * information screen ("scripts blocked: yes") and the §11 status line, so the
 * user learns what was removed rather than silently receiving a different
 * document than the sender wrote.
 */

/** Attribute value schemes that may load or execute something. */
export const DANGEROUS_URL_SCHEMES: readonly string[] = Object.freeze([
  'javascript:', 'vbscript:', 'data:text/html', 'data:image/svg+xml', 'file:',
]);

/** URL schemes safe to keep in rendered markup. */
export const ALLOWED_URL_SCHEMES: readonly string[] = Object.freeze([
  'https:', 'http:', 'mailto:', 'tel:',
]);

/**
 * Allow-list, not deny-list.
 *
 * A deny-list of dangerous tags is wrong by construction: the set grows with
 * every browser release, and anything unforeseen is permitted by default.
 */
export const MARKUP_POLICY = Object.freeze({
  html: Object.freeze({
    allowedTags: Object.freeze([
      'a', 'abbr', 'address', 'article', 'aside', 'b', 'blockquote', 'br', 'caption',
      'cite', 'code', 'col', 'colgroup', 'dd', 'del', 'details', 'dfn', 'div', 'dl',
      'dt', 'em', 'figcaption', 'figure', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5',
      'h6', 'header', 'hr', 'i', 'img', 'ins', 'kbd', 'li', 'main', 'mark', 'nav',
      'ol', 'p', 'pre', 'q', 's', 'samp', 'section', 'small', 'span', 'strong',
      'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead',
      'time', 'tr', 'u', 'ul', 'var', 'wbr',
    ]),
    allowedAttributes: Object.freeze([
      'href', 'src', 'alt', 'title', 'colspan', 'rowspan', 'lang', 'dir',
      'width', 'height', 'datetime', 'cite',
    ]),
  }),
  svg: Object.freeze({
    allowedTags: Object.freeze([
      'svg', 'g', 'defs', 'title', 'desc', 'path', 'rect', 'circle', 'ellipse',
      'line', 'polyline', 'polygon', 'text', 'tspan', 'image', 'clipPath',
      'linearGradient', 'radialGradient', 'stop', 'mask', 'pattern', 'symbol', 'use',
    ]),
    allowedAttributes: Object.freeze([
      'viewBox', 'width', 'height', 'x', 'y', 'cx', 'cy', 'r', 'rx', 'ry',
      'd', 'points', 'x1', 'y1', 'x2', 'y2', 'fill', 'stroke', 'stroke-width',
      'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'opacity',
      'fill-opacity', 'stroke-opacity', 'transform', 'gradientUnits', 'offset',
      'stop-color', 'stop-opacity', 'font-family', 'font-size', 'text-anchor',
      'clip-path', 'mask', 'id', 'class', 'style',
    ]),
    /**
     * `<foreignObject>` embeds arbitrary HTML inside SVG and is a standard
     * sanitiser bypass; `<script>` is obvious. Both are excluded from the
     * allow-list above — listed here only to make the intent searchable.
     */
    explicitlyExcluded: Object.freeze(['script', 'foreignObject', 'animate', 'set', 'handler']),
  }),
});

export type MarkupFindingKind =
  | 'script-element'
  | 'event-handler'
  | 'javascript-url'
  | 'foreign-object'
  | 'external-reference'
  | 'embedded-frame'
  | 'meta-refresh'
  | 'inline-style-import';

export interface MarkupFinding {
  readonly kind: MarkupFindingKind;
  readonly count: number;
  /** Short excerpts for the details view. Never logged (§29). */
  readonly samples: readonly string[];
}

export interface MarkupReport {
  readonly findings: readonly MarkupFinding[];
  /** True when anything executable was present and will be removed. */
  readonly hasActiveContent: boolean;
  /** True when the file would otherwise reach out to the network (§29). */
  readonly hasRemoteReferences: boolean;
}

interface Probe {
  readonly kind: MarkupFindingKind;
  readonly re: RegExp;
}

const PROBES: readonly Probe[] = [
  { kind: 'script-element', re: /<script\b[^>]*>/gi },
  // Any on* attribute. Matching the attribute name rather than a fixed list
  // keeps new event types covered without a maintenance treadmill.
  { kind: 'event-handler', re: /\son[a-z]+\s*=\s*["'][^"']*["']/gi },
  { kind: 'javascript-url', re: /(?:href|src|xlink:href)\s*=\s*["']\s*(?:javascript|vbscript):[^"']*["']/gi },
  { kind: 'foreign-object', re: /<foreignObject\b[^>]*>/gi },
  { kind: 'embedded-frame', re: /<(?:iframe|frame|object|embed|portal)\b[^>]*>/gi },
  { kind: 'meta-refresh', re: /<meta\b[^>]*http-equiv\s*=\s*["']refresh["'][^>]*>/gi },
  { kind: 'inline-style-import', re: /@import\s+(?:url\()?["']?https?:/gi },
  { kind: 'external-reference', re: /(?:href|src|xlink:href)\s*=\s*["']https?:\/\/[^"']+["']/gi },
];

const MAX_SAMPLES = 3;
const SAMPLE_LENGTH = 80;

/**
 * Report what a markup file contains.
 *
 * Regex-based by design: this is a *detector*, and over-reporting is harmless
 * where under-sanitising would not be. Nothing here is trusted to make the file
 * safe — {@link MARKUP_POLICY} does that, through a real parser at the UI layer.
 */
export function scanMarkup(text: string): MarkupReport {
  const findings: MarkupFinding[] = [];

  for (const probe of PROBES) {
    probe.re.lastIndex = 0;
    const samples: string[] = [];
    let count = 0;
    let m: RegExpExecArray | null;
    while ((m = probe.re.exec(text)) !== null) {
      count++;
      if (samples.length < MAX_SAMPLES) samples.push(m[0].slice(0, SAMPLE_LENGTH));
      // Zero-length matches would spin forever; advance defensively.
      if (m.index === probe.re.lastIndex) probe.re.lastIndex++;
    }
    if (count > 0) findings.push({ kind: probe.kind, count, samples });
  }

  const activeKinds = new Set<MarkupFindingKind>([
    'script-element', 'event-handler', 'javascript-url', 'foreign-object',
    'embedded-frame', 'meta-refresh',
  ]);
  const remoteKinds = new Set<MarkupFindingKind>([
    'external-reference', 'inline-style-import', 'embedded-frame',
  ]);

  return {
    findings,
    hasActiveContent: findings.some((f) => activeKinds.has(f.kind)),
    hasRemoteReferences: findings.some((f) => remoteKinds.has(f.kind)),
  };
}

/** True when a URL may be kept in rendered markup. */
export function isUrlAllowed(url: string): boolean {
  const trimmed = url.trim().toLowerCase();
  // Relative URLs carry no scheme and are resolved against a sandboxed base.
  if (!/^[a-z][a-z0-9+.-]*:/.test(trimmed)) return true;
  if (DANGEROUS_URL_SCHEMES.some((s) => trimmed.startsWith(s))) return false;
  return ALLOWED_URL_SCHEMES.some((s) => trimmed.startsWith(s));
}

/**
 * Content-Security-Policy for the sandboxed frame that renders documents.
 *
 * Belt and braces: even if sanitisation were bypassed, this frame cannot
 * execute script or reach the network, which is what §30 and §29 actually
 * require. `default-src 'none'` means anything not listed is denied.
 */
export const RENDER_SANDBOX_CSP = [
  "default-src 'none'",
  "img-src data: blob:",
  "style-src 'unsafe-inline'",
  "font-src data:",
  "media-src blob:",
  "form-action 'none'",
  "frame-ancestors 'none'",
  "base-uri 'none'",
  "script-src 'none'",
  "connect-src 'none'",
].join('; ');
